import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { Check, Zap, CreditCard, AlertCircle, TrendingUp } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  freeQuota: number;
  pricePerCall: number;
  features: string[];
}

interface CreditBalance {
  balance: number;
  totalUsed: number;
  lastActivity: string | null;
}

interface Subscription {
  planId: string;
  status: string;
  renewsAt?: string;
  endsAt?: string;
  cancelAtPeriodEnd?: boolean;
}

interface TaskDetUsage {
  freeQuotaRemaining: number;
  purchasedCallsBalance: number;
  currentPeriodCount: number;
}

// Monthly prices for plans (managed in Stripe)
const PLAN_PRICES: Record<string, number> = {
  'plan_starter': 10,
  'plan_pro': 100,
  'plan_team': 400,
  'plan_business': 1500
};

// Generate features from plan data
const generateFeatures = (plan: any): string[] => {
  const features: string[] = [
    `${plan.freeTaskDetQuota.toLocaleString()} free task determinations/month`,
    `$${plan.taskDetPricePerCall.toFixed(4)} per call after quota (from credits)`,
    'Quota resets monthly',
  ];

  if (plan.freeTaskDetQuota >= 50000) {
    features.push('Discounted rate vs pay-as-you-go');
  }

  if (plan.name === 'team' || plan.name === 'business') {
    features.push('Priority support');
  }

  if (plan.name === 'business') {
    features.push('Dedicated support');
    features.push('Best discounted rate');
  }

  features.push('Cancel anytime');

  return features;
};

export default function PlansAndBilling() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [paygPricePerCall, setPaygPricePerCall] = useState<number>(0.0035);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [taskDetUsage, setTaskDetUsage] = useState<TaskDetUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [calcVolume, setCalcVolume] = useState(50000);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansResponse, creditData, subscriptionData, usageData] = await Promise.all([
        apiService.getTaskDetPlans(),
        apiService.getCreditBalance().catch(() => ({ balance: 0, totalUsed: 0, lastActivity: null })),
        apiService.getTaskDetSubscription().catch(() => null),
        apiService.getTaskDetUsage().catch(() => ({ freeQuotaRemaining: 0, purchasedCallsBalance: 0, currentPeriodCount: 0 })),
      ]);

      // Process plans from API
      // Note: apiService.request() already unwraps the response, so plansResponse = { plans, currentPlanId }
      const apiPlans = (plansResponse as any).plans || [];
      const defaultPlan = apiPlans.find((p: any) => p.isDefault);
      const subscriptionPlans = apiPlans
        .filter((p: any) => !p.isDefault && p.isActive)
        .map((p: any): Plan => ({
          id: p.id,
          name: p.name,
          displayName: p.displayName,
          description: p.description,
          price: PLAN_PRICES[p.id] || 0,
          freeQuota: p.freeTaskDetQuota,
          pricePerCall: p.taskDetPricePerCall,
          features: generateFeatures(p)
        }))
        .sort((a: Plan, b: Plan) => a.price - b.price); // Sort by price: low to high

      setPlans(subscriptionPlans);
      if (defaultPlan) {
        setPaygPricePerCall(defaultPlan.taskDetPricePerCall);
      }

      setCredits(creditData);
      setSubscription(subscriptionData);
      setTaskDetUsage(usageData);
    } catch (err: any) {
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    setSubscribing(planId);
    try {
      const result = await apiService.createTaskDetSubscription(planId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription');
      setSubscribing(null);
    }
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount < 5 || amount > 100) {
      setError('Amount must be between $5 and $100');
      return;
    }

    setTopUpLoading(true);
    try {
      const result = await apiService.createCreditCheckout(amount, 'USD');
      window.location.href = result.checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout');
      setTopUpLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await apiService.createTaskDetBillingPortal();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentPlan = plans.find(p => p.id === subscription?.planId);
  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';

  const calcTiers = [
    { name: 'Pay-as-you-go', monthly: 0, included: 0, rate: paygPricePerCall },
    ...plans.map(p => ({ name: p.displayName, monthly: p.price, included: p.freeQuota, rate: p.pricePerCall }))
  ].map(tier => {
    const totalCost = tier.monthly + Math.max(0, calcVolume - tier.included) * tier.rate;
    const effectiveRate = calcVolume > 0 ? totalCost / calcVolume : 0;
    return { ...tier, totalCost, effectiveRate };
  });

  const cheapestCalcIndex = calcVolume === 0
    ? 0
    : calcTiers.reduce((minIdx, r, idx, arr) => r.totalCost < arr[minIdx].totalCost ? idx : minIdx, 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Plans & Billing</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your API credits and subscription plans
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Current Status Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Current Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Credits Card */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="h-6 w-6" />
              <h3 className="text-lg font-semibold">API Credits</h3>
            </div>
            <div className="mb-4">
              <div className="text-3xl font-bold">${credits?.balance?.toFixed(4) || '0.0000'}</div>
              <div className="text-sm text-indigo-100 mt-1">Current Balance</div>
            </div>
            <div className="flex items-center justify-between text-sm text-indigo-100 mb-4">
              <span>Total Used</span>
              <span className="font-medium">${credits?.totalUsed?.toFixed(4) || '0.0000'}</span>
            </div>
            <button
              onClick={() => setShowTopUpModal(true)}
              className="w-full py-2 px-4 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 transition-colors"
            >
              Top-up Credits
            </button>
          </div>

          {/* Subscription Card */}
          {hasActiveSubscription ? (
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Check className="h-6 w-6" />
                <h3 className="text-lg font-semibold">Active Subscription</h3>
              </div>
              <div className="mb-4">
                <div className="text-2xl font-bold">{currentPlan?.displayName || 'Subscribed'}</div>
                <div className="text-sm text-purple-100 mt-1">
                  {subscription?.cancelAtPeriodEnd
                    ? subscription.endsAt
                      ? `Ends on: ${new Date(subscription.endsAt).toLocaleDateString()}`
                      : 'Cancelled — ends at period end'
                    : subscription?.renewsAt
                      ? `Renews on: ${new Date(subscription.renewsAt).toLocaleDateString()}`
                      : 'Active'
                  }
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm text-purple-100">
                  <span>Free Quota</span>
                  <span className="font-medium">
                    {taskDetUsage?.freeQuotaRemaining || 0} / {currentPlan?.freeQuota || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-purple-100 pt-2 border-t border-purple-400 border-opacity-30">
                  <span>Task Determination rate</span>
                  <span className="font-medium">
                    ${currentPlan?.pricePerCall?.toFixed(4) || '0.0000'}/call
                  </span>
                </div>
              </div>
              <button
                onClick={handleManageBilling}
                className="w-full py-2 px-4 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
              >
                Manage Billing
              </button>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-lg p-6 border-2 border-dashed border-gray-300">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="h-6 w-6 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">No Active Subscription</h3>
              </div>
              <div className="mb-4">
                <div className="text-2xl font-bold text-gray-900">Pay-as-you-go</div>
                <div className="text-sm text-gray-600 mt-1">${paygPricePerCall.toFixed(4)} per task determination (from API credits)</div>
              </div>
              <div className="text-sm text-gray-700 mb-4">
                Subscribe to a plan for free monthly quota and discounted rates. Save 20-33% per call after quota!
              </div>
              <button
                onClick={() => document.getElementById('available-plans')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                View Plans
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Available Plans Section */}
      <div id="available-plans">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Subscription Plans</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {plans.map((plan) => {
            const isCurrentPlan = plan.id === subscription?.planId;
            const isPremium = plan.name === 'pro';

            return (
              <div
                key={plan.id}
                className={`relative rounded-lg shadow-lg flex flex-col ${
                  isPremium ? 'border-2 border-blue-500' : 'border border-gray-200'
                } bg-white`}
              >
                {/* Recommended Badge */}
                {isPremium && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      <Zap size={14} />
                      Recommended
                    </span>
                  </div>
                )}

                <div className="p-6 flex flex-col flex-1">
                  {/* Plan Header */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {plan.displayName}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {plan.description}
                  </p>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-gray-900">
                        ${plan.price}
                      </span>
                      <span className="text-gray-500 text-sm">/month</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Billed monthly
                    </p>
                  </div>

                  {/* Features — grows to fill space */}
                  <ul className="space-y-3 mb-6 flex-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button — always at bottom */}
                  {isCurrentPlan ? (
                    <button
                      disabled
                      className="w-full py-3 px-4 bg-gray-100 text-gray-600 rounded-lg font-semibold cursor-not-allowed"
                    >
                      Current Plan
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={subscribing === plan.id}
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                        isPremium
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-800 text-white hover:bg-gray-900'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {subscribing === plan.id ? 'Processing...' : 'Subscribe Now'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pay-as-you-go Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Pay-as-you-go</h3>
        <p className="text-sm text-gray-600 mb-4">
          Not ready to commit? Stay on the pay-as-you-go plan and only pay for what you use.
        </p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <Check className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700">No monthly fee</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700">${paygPricePerCall.toFixed(4)} per task determination (charged from API credits)</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700">No quota resets</span>
          </li>
        </ul>
      </div>

      {/* Pricing Calculator */}
      {plans.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Find Your Optimal Tier</h2>
          <p className="text-sm text-gray-500 mb-6">
            Enter your expected monthly task determination volume to see the cost at each tier.
          </p>

          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-1">
              <input
                type="range"
                min={0}
                max={800000}
                step={1000}
                value={Math.min(calcVolume, 800000)}
                onChange={(e) => setCalcVolume(Number(e.target.value))}
                aria-label="Monthly task determinations"
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-200 accent-blue-600"
                style={{ minHeight: '44px' }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0</span>
                <span>800,000</span>
              </div>
            </div>
            <div className="w-full md:w-36">
              <input
                type="number"
                min={0}
                step={1000}
                value={calcVolume}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val) && val >= 0) setCalcVolume(val);
                }}
                aria-label="Monthly task determinations"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {calcVolume === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              Enter your expected monthly call volume to compare tiers.
            </p>
          ) : (
            <ul className="space-y-1">
              {calcTiers.map((r, idx) => {
                const isCheapest = idx === cheapestCalcIndex;
                return (
                  <li
                    key={r.name}
                    className={`flex items-center justify-between py-3 px-4 rounded-lg transition-colors ${
                      isCheapest
                        ? 'bg-blue-50 border-l-2 border-blue-500'
                        : 'border-l-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">{r.name}</span>
                      {isCheapest && (
                        <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                          Best value
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900">
                        ${r.totalCost >= 1000
                          ? r.totalCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                          : r.totalCost.toFixed(2)}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">/month</span>
                      <div className="text-sm text-gray-500">${r.effectiveRate.toFixed(4)}/call effective</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="text-xs text-gray-400 mt-4">
            Costs shown are for task determinations only. Model inference is charged separately at provider token rates.
          </p>
        </div>
      )}

      {/* FAQ Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Frequently Asked Questions</h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-blue-900 mb-1">How does billing work?</h4>
            <p className="text-sm text-blue-800">
              You're billed monthly from your subscription date. Your free quota resets each month on the same day.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-blue-900 mb-1">What's the difference between API credits and subscriptions?</h4>
            <p className="text-sm text-blue-800">
              API credits are used for AI model costs (responses, reasoning, etc.). Subscriptions give you free monthly quota for task determinations at a discounted rate.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-blue-900 mb-1">What happens if I exceed my quota?</h4>
            <p className="text-sm text-blue-800">
              After your free quota is used, task determinations are charged from your API credits at your plan's discounted rate. No subscription? Pay-as-you-go at ${paygPricePerCall.toFixed(4)}/call.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Can I cancel anytime?</h4>
            <p className="text-sm text-blue-800">
              Yes! You can cancel anytime via the billing portal. Your subscription remains active until the end of your billing period.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Us */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Us</h3>
        <p className="text-sm text-gray-600 mb-4">
          Have questions about pricing or need help choosing the right plan? We'd love to hear from you — reach out and we'll get back to you as soon as possible.
        </p>
        <a
          href="mailto:contact@fenxlabs.ai"
          className="inline-flex items-center gap-2 text-blue-600 font-medium hover:text-blue-700 hover:underline text-sm"
        >
          contact@fenxlabs.ai
        </a>
      </div>

      {/* Top-up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Top-up API Credits
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Amount (USD)
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="5"
                    max="100"
                    placeholder="Enter amount"
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <div className="mt-1 text-sm text-gray-500">
                    Min: $5 | Max: $100
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTopUpModal(false);
                    setTopUpAmount('');
                    setError('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTopUp}
                  disabled={topUpLoading || !topUpAmount || parseFloat(topUpAmount) < 5 || parseFloat(topUpAmount) > 100}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {topUpLoading ? 'Redirecting...' : 'Pay with Stripe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
